import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import Select, { createFilter } from "react-select";
import Swal from "sweetalert2";

const otherDepartmentOption = { value: "__other_department__", label: "Other (type manually)" };
const filterDepartment = createFilter();

const emptyForm = {
  workerCode: "",
  workerName: "",
  logicAccountCode: "",
  logicAccountName: "",
  department: "",
  reason: "",
  qty: "",
  amount: "",
};

export default function DebitNoteForm() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [payees, setPayees] = useState([]);
  const [searching, setSearching] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isOtherDepartment, setIsOtherDepartment] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    axiosInstance.get("/api/debit-notes/departments")
      .then(response => setDepartments((response.data || []).map(department => ({ value: department, label: department }))))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    if (form.logicAccountCode || searchTerm.trim().length < 2) {
      setPayees([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const response = await axiosInstance.get("/api/paid-to/payee-search", { params: { q: searchTerm.trim() } });
        if (!cancelled) setPayees(response.data || []);
      } catch (_) {
        if (!cancelled) setPayees([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.logicAccountCode, searchTerm]);

  const updateField = event => {
    const { name, value } = event.target;
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: "" }));
  };

  const updateAmount = event => {
    const value = event.target.value;
    if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
      setForm(current => ({ ...current, amount: value }));
      setErrors(current => ({ ...current, amount: "" }));
    }
  };

  const updatePayeeSearch = event => {
    setSearchTerm(event.target.value);
    setForm(current => ({
      ...current,
      workerCode: "",
      workerName: "",
      logicAccountCode: "",
      logicAccountName: "",
    }));
    setErrors(current => ({ ...current, logicAccountCode: "" }));
  };

  const selectPayee = payee => {
    setForm(current => ({
      ...current,
      workerCode: payee.WORKER_CODE || "",
      workerName: payee.WORKER_NAME || "",
      logicAccountCode: payee.LOGIC_ACCOUNT_CODE || "",
      logicAccountName: payee.LOGIC_ACCOUNT_NAME || "",
    }));
    setSearchTerm(`${payee.LOGIC_ACCOUNT_CODE} - ${payee.LOGIC_ACCOUNT_NAME}`);
    setPayees([]);
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.logicAccountCode) nextErrors.logicAccountCode = "Select a Logic Code / Tally Name from search results.";
    if (!form.department.trim()) nextErrors.department = "Department is required.";
    if (!form.reason.trim()) nextErrors.reason = "Reason is required.";
    if (form.qty && (!Number.isInteger(Number(form.qty)) || Number(form.qty) < 1)) nextErrors.qty = "Enter a positive whole quantity.";
    if (!/^\d+(\.\d{1,2})?$/.test(form.amount) || Number(form.amount) <= 0 || Number(form.amount) > 99999999.99) nextErrors.amount = "Enter a valid amount with maximum 2 decimal places.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!validate()) return;
    submittingRef.current = true;
    try {
      setSaving(true);
      const response = await axiosInstance.post("/api/debit-notes", {
        logicAccountCode: form.logicAccountCode,
        department: form.department.trim(),
        isOtherDepartment,
        reason: form.reason,
        qty: form.qty,
        amount: form.amount,
      });
      await Swal.fire({ icon: "success", title: "Debit Note Saved", text: `Debit Note #${response.data.id} created successfully.`, confirmButtonColor: "#c9434a" });
      setForm(emptyForm);
      setIsOtherDepartment(false);
      setSearchTerm("");
      setPayees([]);
      setErrors({});
    } catch (error) {
      await Swal.fire({ icon: "error", title: "Unable to Save", text: error.response?.data?.error || "Debit note could not be saved.", confirmButtonColor: "#c9434a" });
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}><Sidebar /></div>
      <div className="flex-grow-1">
        <Header toggleSidebar={() => setIsSidebarVisible(value => !value)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-4">
          <h3 className="mb-4">Debit Note</h3>
          <div className="card shadow border-0">
            <div className="card-header bg-dark text-white">Create Debit Note</div>
            <div className="card-body">
              <form onSubmit={handleSubmit} noValidate>
                <div className="row">
                  <div className="col-12 mb-3 position-relative">
                    <label className="form-label" htmlFor="debit-payee-search">Search Logic Code / Tally Name</label>
                    <input id="debit-payee-search" className={`form-control ${errors.logicAccountCode ? "is-invalid" : ""}`} value={searchTerm} onChange={updatePayeeSearch} placeholder="Logic Code ya Tally Name type karein..." autoComplete="off" />
                    {errors.logicAccountCode && <div className="invalid-feedback">{errors.logicAccountCode}</div>}
                    {searching && <div className="form-text">Searching...</div>}
                    {payees.length > 0 && (
                      <div className="list-group position-absolute start-0 end-0 mx-2 shadow" style={{ zIndex: 1000, maxHeight: 240, overflowY: "auto" }}>
                        {payees.map((payee, index) => (
                          <button key={`${payee.LOGIC_ACCOUNT_CODE}-${index}`} type="button" className="list-group-item list-group-item-action" onClick={() => selectPayee(payee)}>
                            {payee.LOGIC_ACCOUNT_CODE} - {payee.LOGIC_ACCOUNT_NAME}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="debit-worker-code">Worker Code</label>
                    <input id="debit-worker-code" className="form-control bg-light" value={form.workerCode} disabled />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="debit-worker-name">Worker Name</label>
                    <input id="debit-worker-name" className="form-control bg-light" value={form.workerName} disabled />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="debit-logic-code">Logic Account Code</label>
                    <input id="debit-logic-code" className="form-control bg-light" value={form.logicAccountCode} disabled />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label" htmlFor="debit-logic-name">Logic Account Name</label>
                    <input id="debit-logic-name" className="form-control bg-light" value={form.logicAccountName} disabled />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label" htmlFor="debit-department">Department</label>
                    <Select
                      inputId="debit-department"
                      options={[...departments, otherDepartmentOption]}
                      value={isOtherDepartment ? otherDepartmentOption : departments.find(option => option.value === form.department) || null}
                      onChange={option => {
                        const isOther = option === otherDepartmentOption;
                        setIsOtherDepartment(isOther);
                        setForm(current => ({ ...current, department: isOther ? "" : option?.value || "" }));
                        setErrors(current => ({ ...current, department: "" }));
                      }}
                      filterOption={(option, input) => option.data === otherDepartmentOption || filterDepartment(option, input)}
                      isSearchable isClearable isDisabled={saving}
                      placeholder="Department search karein..."
                    />
                    {isOtherDepartment && (
                      <div className="mt-2">
                        <label className="form-label" htmlFor="debit-other-department">Other Department</label>
                        <input id="debit-other-department" name="department" className={`form-control ${errors.department ? "is-invalid" : ""}`} value={form.department} onChange={updateField} placeholder="Department ka naam type karein..." maxLength={255} disabled={saving} autoFocus />
                      </div>
                    )}
                    {errors.department && <div className="text-danger small mt-1">{errors.department}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="debit-qty">Quantity <span className="text-muted">(optional)</span></label>
                    <input id="debit-qty" name="qty" type="number" min="1" step="1" className={`form-control ${errors.qty ? "is-invalid" : ""}`} value={form.qty} onChange={updateField} />
                    {errors.qty && <div className="invalid-feedback">{errors.qty}</div>}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label" htmlFor="debit-amount">Amount</label>
                    <input id="debit-amount" name="amount" type="number" min="0.01" max="99999999.99" step="0.01" className={`form-control ${errors.amount ? "is-invalid" : ""}`} value={form.amount} onChange={updateAmount} disabled={saving} />
                    {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                  </div>
                  <div className="col-12 mb-3">
                    <label className="form-label" htmlFor="debit-reason">Reason</label>
                    <textarea id="debit-reason" name="reason" rows="4" className={`form-control ${errors.reason ? "is-invalid" : ""}`} value={form.reason} onChange={updateField} maxLength="500" />
                    {errors.reason && <div className="invalid-feedback">{errors.reason}</div>}
                  </div>
                </div>
                <div className="text-end"><button type="submit" className="btn btn-danger" disabled={saving}>{saving ? "Saving..." : "Save Debit Note"}</button></div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}